<template>
  <div class="load-order-builder">
    <section class="page-body fade-in" v-if="!initialLoading">
      <div class="card-block">
        <div class="block-header">
          <div class="block-actions">
            <button
              class="btn btn-outline"
              @click="refreshAll"
              :disabled="refreshing || itemSubmitting"
            >
              <i class="bi bi-arrow-clockwise"></i>
              <span>Actualiser</span>
            </button>
          </div>
        </div>

        <div class="product-selection">
          <div v-if="itemsLoading" class="product-state">
            <div class="loader"></div>
            <p>Chargement des produits…</p>
          </div>
          <div v-else-if="!productOptions.length" class="product-state">
            <i class="bi bi-box-seam"></i>
            <p>Aucun produit disponible pour le moment.</p>
          </div>
          <div v-else>
            <div class="search-wrapper">
              <div class="search-input-group">
                <i class="bi bi-search"></i>
                <input
                  v-model.trim="searchQuery"
                  type="text"
                  class="search-input"
                  placeholder="Rechercher des produits par nom ou référence…"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="btn-clear-search"
                  @click="searchQuery = ''"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
              <p v-if="searchQuery" class="search-results-info">
                Affichage de {{ filteredProducts.length }} sur
                {{ productOptions.length }} produits
              </p>
            </div>
            <div v-if="!searchQuery.trim()" class="product-state">
              <i class="bi bi-search"></i>
              <p>
                Entrez un terme de recherche pour trouver des produits
                (affichage jusqu'à 6 résultats)
              </p>
            </div>
            <div v-else-if="!filteredProducts.length" class="product-state">
              <i class="bi bi-search"></i>
              <p>Aucun produit trouvé correspondant à "{{ searchQuery }}"</p>
            </div>
            <div v-else class="product-grid">
              <button
                v-for="product in filteredProducts"
                :key="product.reference || product.id || product.uuid"
                type="button"
                class="product-card"
                @click="openProductModal(product)"
                :disabled="itemSubmitting"
              >
                <div class="product-image-wrapper">
                  <img
                    v-if="getImagePath(product)"
                    :src="$imageUrl(getImagePath(product))"
                    :alt="product.fullName"
                    @error="handleImageError($event)"
                  />
                  <div v-else class="product-placeholder">
                    <i class="bi bi-image"></i>
                  </div>
                </div>
                <div class="product-card-body">
                  <h4>{{ product.fullName }}</h4>
                  <span class="product-price">
                    {{ formatAmount(getProductPrice(product)) }}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div class="items-cards-wrapper">
          <div v-if="itemsLoading" class="items-state">
            <div class="loader"></div>
            <p>Chargement des articles ajoutés…</p>
          </div>
          <div v-else-if="!preparedOrderItems.length" class="items-state">
            <i class="bi bi-archive"></i>
            <p>Aucun article n'a encore été ajouté.</p>
          </div>
          <div v-else>
            <div class="items-cards">
              <div
                v-for="line in preparedOrderItems"
                :key="line.id"
                class="item-card"
              >
                <div class="item-image-wrapper">
                  <img
                    v-if="getImagePathFromLine(line)"
                    :src="$imageUrl(getImagePathFromLine(line))"
                    :alt="line.designation"
                    @error="handleImageError($event)"
                  />
                  <div v-else class="item-image-placeholder">
                    <i class="bi bi-image"></i>
                  </div>
                </div>
                <div class="item-card-content">
                  <div class="item-main-info">
                    <h4>{{ line.designation }}</h4>
                    <small v-if="line.reference" class="item-reference"
                      >Réf. {{ line.reference }}</small
                    >
                  </div>
                  <div class="item-details">
                    <div class="item-detail">
                      <span class="detail-label">Prix unit.</span>
                      <span class="detail-value">{{
                        formatAmount(line.price)
                      }}</span>
                    </div>
                    <div class="item-detail">
                      <span class="detail-label">Qté</span>
                      <span class="detail-value">{{
                        formatQuantity(line.quantity)
                      }}</span>
                    </div>
                    <div class="item-detail item-detail-total">
                      <span class="detail-label">Total</span>
                      <span class="detail-value total-value">{{
                        formatAmount(line.total)
                      }}</span>
                    </div>
                  </div>
                </div>
                <button
                  class="btn-delete-item"
                  @click="deleteItem(line.raw)"
                  :disabled="itemSubmitting"
                  title="Supprimer"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
            <div class="items-summary">
              <div class="summary-total">
                <span>Total général</span>
                <strong>{{ formatAmount(totalAmount) }}</strong>
              </div>
              <div class="summary-count">
                <span>{{ preparedOrderItems.length }} article(s)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showProductModal && activeProduct"
        class="product-modal-overlay"
        @click="closeProductModal"
      >
        <div class="product-modal" @click.stop>
          <header class="product-modal-header">
            <div>
              <h3>{{ activeProduct.fullName }}</h3>
              <p>
                Prix unitaire (TTC) :
                {{ formatAmount(getProductPrice(activeProduct)) }}
              </p>
            </div>
            <button class="btn btn-light icon-only" @click="closeProductModal">
              <i class="bi bi-x-lg"></i>
            </button>
          </header>

          <section class="product-modal-body">
            <div v-if="getImagePath(activeProduct)" class="product-modal-image">
              <img
                :src="$imageUrl(getImagePath(activeProduct))"
                :alt="activeProduct.fullName"
                @error="handleImageError($event)"
              />
            </div>

            <div class="quantity-panels">
              <div class="quantity-display">
                <span>Quantité (KG)</span>
                <strong>{{ modalQuantity || "0" }}</strong>
              </div>
              <div class="quantity-display secondary">
                <span>Total (TTC)</span>
                <strong>{{ formatAmount(modalTotalAmount) }}</strong>
              </div>
            </div>

            <div class="quantity-keyboard">
              <button
                v-for="key in keypadLayout"
                :key="key.label"
                type="button"
                class="btn btn-keyboard"
                :class="{
                  danger: key.type === 'clear',
                  action: key.type === 'submit',
                }"
                @click="handleKeypadPress(key)"
                :disabled="itemSubmitting && key.type === 'submit'"
              >
                {{ key.label }}
              </button>
            </div>

            <div class="remark-group">
              <label for="modal-remark">Notes (optionnel)</label>
              <textarea
                id="modal-remark"
                v-model.trim="modalRemark"
                rows="2"
                placeholder="Ajouter une note pour cet article…"
              ></textarea>
            </div>
          </section>

          <footer class="product-modal-footer">
            <button
              class="btn btn-light"
              type="button"
              @click="closeProductModal"
            >
              Annuler
            </button>
            <button
              class="btn btn-success"
              type="button"
              :disabled="!canConfirmModal || itemSubmitting"
              @click="confirmProductSelection"
            >
              <i class="bi bi-check-circle"></i>
              <span v-if="!itemSubmitting">Ajouter</span>
              <span v-else>Ajout en cours…</span>
            </button>
          </footer>
        </div>
      </div>

      <div class="card-block">
        <h2>Détails du Bon de Charge</h2>
        <p class="block-subtitle">
          Renseignez les informations principales. Les montants se mettent à
          jour automatiquement.
        </p>

        <form class="order-form" @submit.prevent="saveOrder">
          <div class="form-group full">
            <label>Notes</label>
            <textarea
              v-model.trim="form.notes"
              rows="3"
              class="form-control"
              placeholder="Instructions ou notes supplémentaires…"
            ></textarea>
          </div>

          <div class="summary-card">
            <div>
              <span>Total (TTC)</span>
              <strong>{{ formatAmount(totalAmount) }}</strong>
            </div>
            <div>
              <span>Nombre d'articles</span>
              <strong>{{ preparedOrderItems.length }}</strong>
            </div>
          </div>

          <p v-if="formErrors.items" class="error form-error-inline">
            {{ formErrors.items }}
          </p>

          <div class="form-actions">
            <button
              type="button"
              class="btn btn-light"
              @click="goBack"
              :disabled="submitting"
            >
              Annuler
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="submitting || !canSubmit"
            >
              <i class="bi bi-check-circle"></i>
              <span v-if="!submitting">Enregistrer</span>
              <span v-else>Enregistrement en cours…</span>
            </button>
          </div>
        </form>
      </div>

      <p class="hint">
        <span class="text-danger">*</span> Champ obligatoire &nbsp;•&nbsp; Le
        montant total est calculé à partir des articles ajoutés.
      </p>
    </section>

    <section class="loading-state" v-else>
      <div class="loader"></div>
      <p>Préparation du formulaire…</p>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "LoadOrderCreate",
  data() {
    const today = new Date().toISOString().split("T")[0];
    return {
      initialLoading: true,
      refreshing: false,
      itemsLoading: false,
      submitting: false,
      itemSubmitting: false,
      adjustingQuantity: false,
      adjustingPcs: false,
      searchQuery: "",
      orderItem: {
        item_reference: "",
        priceTTC: "",
        quantity: "",
        pcs: "",
        remark: "",
      },
      showProductModal: false,
      activeProduct: null,
      modalQuantity: "",
      modalRemark: "",
      imageFallback:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23e2e8f0'/%3E%3Cg fill='%2394a3b8' font-family='sans-serif' font-size='12'%3E%3Ctext x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'%3EAucune image%3C/text%3E%3C/g%3E%3C/svg%3E",
      form: {
        customer_reference: "",
        status: "draft",
        scheduled_date: today,
        notes: "",
        total_amount: 0,
      },
      itemErrors: {},
      formErrors: {},
    };
  },
  computed: {
    ...mapGetters("product", {
      productsRaw: "getAll",
    }),
    ...mapGetters("loadOrderItem", {
      orderItems: "getAll",
    }),
    productList() {
      return Array.isArray(this.productsRaw?.data)
        ? this.productsRaw.data
        : this.productsRaw || [];
    },
    productOptions() {
      return this.productList.map((product) => {
        const reference = product?.reference || "";
        const fullName =
          product?.fullName ||
          product?.name ||
          product?.designation ||
          reference;
        const price =
          product?.priceTTC != null
            ? product?.priceTTC
            : product?.price_ttc != null
            ? product?.price_ttc
            : product?.price != null
            ? product?.price
            : product?.unit_price != null
            ? product?.unit_price
            : 0;
        const pcsCandidate =
          product?.pcs != null
            ? product?.pcs
            : product?.items_per_pack != null
            ? product?.items_per_pack
            : product?.packaging != null
            ? product?.packaging
            : null;
        const normalizedPcs =
          pcsCandidate === null || pcsCandidate === undefined
            ? null
            : Number.isFinite(Number(pcsCandidate))
            ? Number(pcsCandidate)
            : pcsCandidate;
        const image =
          product?.image != null
            ? product?.image
            : product?.image_url != null
            ? product?.image_url
            : product?.photo != null
            ? product?.photo
            : product?.picture != null
            ? product?.picture
            : null;

        return {
          ...product,
          reference,
          fullName,
          priceTTC: price,
          pcs: normalizedPcs,
          image,
        };
      });
    },
    filteredProducts() {
      if (!this.searchQuery.trim()) {
        return [];
      }
      const query = this.searchQuery.toLowerCase().trim();
      return this.productOptions.filter((product) => {
        const fullName = (product.fullName || "").toLowerCase();
        const reference = (product.reference || "").toLowerCase();
        const name = (product.name || "").toLowerCase();
        const designation = (product.designation || "").toLowerCase();
        return (
          fullName.includes(query) ||
          reference.includes(query) ||
          name.includes(query) ||
          designation.includes(query)
        );
      });
    },
    selectedProduct() {
      return (
        this.productOptions.find(
          (product) => product.reference === this.orderItem.item_reference
        ) || null
      );
    },
    preparedOrderItems() {
      return (this.orderItems || []).map((item) => {
        const quantity = Number(
          item.quantity != null
            ? item.quantity
            : item.qty != null
            ? item.qty
            : 0
        );
        const price = Number(
          item.priceTTC != null
            ? item.priceTTC
            : item.price_ttc != null
            ? item.price_ttc
            : item.unit_price != null
            ? item.unit_price
            : 0
        );
        const total = Number(
          item.totalPriceTTC != null
            ? item.totalPriceTTC
            : item.total_price_ttc != null
            ? item.total_price_ttc
            : price * quantity
        );
        return {
          id:
            item.id ||
            item.uuid ||
            `${item.item_reference}-${item.id || Date.now()}`,
          reference: item.item_reference || item.reference,
          designation:
            item.itemFullName ||
            item.item_full_name ||
            item.item?.fullName ||
            item.item?.name ||
            item.designation ||
            item.item_reference ||
            "Article",
          price,
          quantity,
          total,
          remark: item.remark || item.note || "",
          raw: item,
        };
      });
    },
    totalAmount() {
      return this.preparedOrderItems.reduce(
        (sum, item) => sum + Number(item.total || 0),
        0
      );
    },
    canSubmit() {
      return (
        !!this.form.customer_reference && this.preparedOrderItems.length > 0
      );
    },
    modalTotalAmount() {
      if (!this.activeProduct) return 0;
      const quantity = Number(this.modalQuantity);
      const price = Number(this.getProductPrice(this.activeProduct) || 0);
      const total = quantity * price;
      return Number.isFinite(total) ? total : 0;
    },
    canConfirmModal() {
      const quantity = Number(this.modalQuantity);
      return (
        !!this.activeProduct &&
        !Number.isNaN(quantity) &&
        quantity > 0 &&
        Number.isFinite(quantity)
      );
    },
    keypadLayout() {
      return [
        { label: "7", value: "7" },
        { label: "8", value: "8" },
        { label: "9", value: "9" },
        { label: "⌫", type: "backspace" },
        { label: "4", value: "4" },
        { label: "5", value: "5" },
        { label: "6", value: "6" },
        { label: "C", type: "clear" },
        { label: "1", value: "1" },
        { label: "2", value: "2" },
        { label: "3", value: "3" },
        { label: "0", value: "0" },
        { label: "00", value: "00" },
        { label: ".", value: "." },
      ];
    },
  },
  watch: {
    selectedProduct: {
      handler(product) {
        if (!product) {
          this.orderItem.priceTTC = "";
          return;
        }
        const price =
          product.priceTTC != null
            ? product.priceTTC
            : product.price_ttc != null
            ? product.price_ttc
            : product.price != null
            ? product.price
            : product.unit_price;
        const numericPrice = Number(price);
        if (!Number.isNaN(numericPrice)) {
          this.orderItem.priceTTC = numericPrice.toFixed(2);
        } else if (price !== undefined && price !== null) {
          this.orderItem.priceTTC = price;
        }
        if (this.orderItem.pcs) {
          this.updateQuantityFromPcs(this.orderItem.pcs);
        } else if (
          this.orderItem.quantity &&
          product.pcs !== undefined &&
          product.pcs !== null
        ) {
          this.updatePcsFromQuantity(this.orderItem.quantity);
        }
      },
    },
    totalAmount: {
      immediate: true,
      handler(value) {
        this.form.total_amount = Number(
          value.toFixed ? value.toFixed(2) : value
        );
      },
    },
    "orderItem.pcs"(newValue) {
      if (this.adjustingPcs) return;
      if (!newValue) return;
      this.updateQuantityFromPcs(newValue);
    },
    "orderItem.quantity"(newValue) {
      if (this.adjustingQuantity) return;
      if (!newValue) return;
      this.updatePcsFromQuantity(newValue);
    },
  },
  methods: {
    openProductModal(product) {
      if (!product) return;
      this.itemErrors = {};
      this.activeProduct = product;
      this.modalQuantity = "";
      this.modalRemark = "";
      this.showProductModal = true;
    },
    closeProductModal() {
      if (this.itemSubmitting) return;
      this.showProductModal = false;
      this.activeProduct = null;
      this.modalQuantity = "";
      this.modalRemark = "";
      this.itemErrors = {};
    },
    goBack() {
      this.$router.back();
    },
    getImagePath(product) {
      if (!product) {
        return "";
      }
      return product.image != null
        ? product.image
        : product.image_url != null
        ? product.image_url
        : product.photo != null
        ? product.photo
        : product.picture != null
        ? product.picture
        : "";
    },
    getImagePathFromLine(line) {
      if (!line) return "";
      // Try to get product from productOptions
      const product = this.productOptions.find(
        (p) => p.reference === line.reference
      );
      if (product) {
        return this.getImagePath(product);
      }
      // Try to get image from raw item data
      const rawItem = line.raw || {};
      const itemData = rawItem.item || {};
      return rawItem.image != null
        ? rawItem.image
        : itemData.image != null
        ? itemData.image
        : itemData.image_url != null
        ? itemData.image_url
        : itemData.photo != null
        ? itemData.photo
        : itemData.picture != null
        ? itemData.picture
        : "";
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.src = this.imageFallback;
      }
    },
    getProductPrice(product) {
      if (!product) return 0;
      const price =
        product.priceTTC != null
          ? product.priceTTC
          : product.price_ttc != null
          ? product.price_ttc
          : product.price != null
          ? product.price
          : product.unit_price != null
          ? product.unit_price
          : 0;
      return Number(price) || 0;
    },
    formatAmount(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatQuantity(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      const hasDecimals = number % 1 !== 0;
      return hasDecimals ? number.toFixed(2) : number.toString();
    },
    handleKeypadPress(key) {
      if (!key) return;
      if (key.type === "clear") {
        this.modalQuantity = "";
        return;
      }
      if (key.type === "backspace") {
        this.modalQuantity = this.modalQuantity.slice(
          0,
          Math.max(0, this.modalQuantity.length - 1)
        );
        return;
      }
      const value = key.value;
      if (value === undefined) {
        return;
      }
      if (value === ".") {
        if (this.modalQuantity.includes(".")) {
          return;
        }
        this.modalQuantity = this.modalQuantity
          ? `${this.modalQuantity}.`
          : "0.";
        return;
      }
      if (value === "00") {
        if (!this.modalQuantity) return;
        this.modalQuantity += "00";
        return;
      }
      if (this.modalQuantity === "0") {
        this.modalQuantity = value;
      } else {
        this.modalQuantity += value;
      }
    },
    async confirmProductSelection() {
      if (!this.activeProduct) return;
      const quantity = Number(this.modalQuantity);
      if (!this.canConfirmModal || Number.isNaN(quantity)) {
        this.itemErrors = {
          ...this.itemErrors,
          quantity: "Entrez une quantité valide.",
        };
        return;
      }
      this.orderItem.item_reference = this.activeProduct.reference || "";
      const price = this.getProductPrice(this.activeProduct);
      const numericPrice = Number(price);
      this.orderItem.priceTTC = Number.isNaN(numericPrice)
        ? price
        : numericPrice.toFixed(2);
      this.orderItem.quantity = quantity;
      this.orderItem.remark = this.modalRemark || "";
      if (!this.orderItem.item_reference) {
        this.$toast?.error?.("Référence produit introuvable.");
        return;
      }
      if (
        this.activeProduct.pcs !== undefined &&
        this.activeProduct.pcs !== null
      ) {
        this.updatePcsFromQuantity(quantity);
      } else {
        this.orderItem.pcs = "";
      }
      const saved = await this.saveItem();
      if (saved) {
        this.searchQuery = "";
        this.closeProductModal();
      }
    },
    async loadInitialData() {
      this.initialLoading = true;
      try {
        this.itemsLoading = true;
        // Auto-set customer_reference from logged-in user
        if (this.$authUser?.reference) {
          this.form.customer_reference = this.$authUser.reference;
        }
        await Promise.all([
          this.$store.dispatch("product/getAll"),
          this.$store.dispatch("loadOrderItem/getAll"),
        ]);
      } catch (error) {
        console.error("Erreur lors de la préparation du bon de charge", error);
        this.$toast?.error?.("Impossible de préparer le formulaire.");
      } finally {
        this.itemsLoading = false;
        this.initialLoading = false;
      }
    },
    async refreshAll() {
      this.refreshing = true;
      try {
        this.itemsLoading = true;
        await Promise.all([
          this.$store.dispatch("product/getAll"),
          this.$store.dispatch("loadOrderItem/getAll"),
        ]);
        this.$toast?.success?.("Liste des produits actualisée.");
      } catch (error) {
        console.error("Erreur lors de l'actualisation des listes", error);
        this.$toast?.error?.("Impossible d'actualiser les listes.");
      } finally {
        this.itemsLoading = false;
        this.refreshing = false;
      }
    },
    validateItem() {
      const errors = {};
      if (!this.orderItem.item_reference) {
        errors.item_reference = "Sélectionnez un produit.";
      }
      const quantity = Number(this.orderItem.quantity);
      if (!this.orderItem.quantity || Number.isNaN(quantity) || quantity <= 0) {
        errors.quantity = "Entrez une quantité valide.";
      }
      const price = Number(this.orderItem.priceTTC || 0);
      if (Number.isNaN(price) || price < 0) {
        errors.priceTTC = "Le prix doit être positif.";
      }
      this.itemErrors = errors;
      return Object.keys(errors).length === 0;
    },
    resetItemForm() {
      this.orderItem = {
        item_reference: "",
        priceTTC: "",
        quantity: "",
        pcs: "",
        remark: "",
      };
      this.itemErrors = {};
    },
    buildItemPayload() {
      const quantity = Number(this.orderItem.quantity);
      const price = Number(this.orderItem.priceTTC || 0);
      return {
        item_reference: this.orderItem.item_reference,
        quantity,
        pcs: this.orderItem.pcs ? Number(this.orderItem.pcs) : null,
        priceTTC: price,
        remark: this.orderItem.remark || "",
        totalPriceTTC: Number((quantity * price).toFixed(2)),
      };
    },
    async saveItem() {
      if (this.itemSubmitting) return false;
      if (!this.validateItem()) return false;
      this.itemSubmitting = true;
      let success = false;
      try {
        const payload = this.buildItemPayload();
        await this.$store.dispatch("loadOrderItem/store", payload);
        await this.$store.dispatch("loadOrderItem/getAll");
        this.resetItemForm();
        this.$toast?.success?.("Article ajouté au bon de charge.");
        success = true;
      } catch (error) {
        console.error("Erreur lors de l'enregistrement de l'article", error);
        this.$toast?.error?.("Impossible d'ajouter l'article.");
      } finally {
        this.itemSubmitting = false;
      }
      return success;
    },
    async deleteItem(item) {
      if (!item) return;
      const reference = item.id || item.reference || item.item_reference;
      if (!reference) return;
      const confirmed = await this.$confirm?.({
        title: "Supprimer l'article",
        message: "Voulez-vous supprimer cet article du bon de charge ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (confirmed === false) {
        return;
      }
      this.itemSubmitting = true;
      try {
        await this.$store.dispatch("loadOrderItem/destroy", reference);
        await this.$store.dispatch("loadOrderItem/getAll");
        this.$toast?.success?.("Article supprimé.");
      } catch (error) {
        console.error("Erreur lors de la suppression de l'article", error);
        this.$toast?.error?.("Impossible de supprimer l'article.");
      } finally {
        this.itemSubmitting = false;
      }
    },
    validateForm() {
      const errors = {};

      if (!this.form.customer_reference) {
        errors.customer_reference =
          "Référence client introuvable. Veuillez vous reconnecter.";
      }
      if (this.preparedOrderItems.length === 0) {
        errors.items = "Ajoutez au moins un article avant d'enregistrer.";
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    buildOrderPayload() {
      return {
        customer_reference: this.form.customer_reference,
        status: this.form.status,
        scheduled_date: this.form.scheduled_date,
        notes: this.form.notes,
        total_amount: this.totalAmount,
      };
    },
    async saveOrder() {
      if (!this.validateForm() || this.submitting) return;
      this.submitting = true;
      try {
        const payload = this.buildOrderPayload();
        await this.$store.dispatch("loadOrder/store", payload);
        this.$toast?.success?.("Bon de charge créé avec succès.");
        this.$router.push({ name: "loadorders-list" });
      } catch (error) {
        console.error("Erreur lors de la création du bon de charge", error);
        this.$toast?.error?.("Impossible de créer le bon de charge.");
      } finally {
        this.submitting = false;
      }
    },
    updateQuantityFromPcs(pcsValue) {
      if (!this.selectedProduct || !pcsValue) return;
      const pcsPerUnit = Number(
        this.selectedProduct.pcs != null
          ? this.selectedProduct.pcs
          : this.selectedProduct.items_per_pack != null
          ? this.selectedProduct.items_per_pack
          : 0
      );
      if (!pcsPerUnit) return;
      this.adjustingQuantity = true;
      const quantity = Number(pcsValue) / pcsPerUnit;
      this.orderItem.quantity = Number.isFinite(quantity)
        ? Number(quantity.toFixed(2))
        : this.orderItem.quantity;
      this.adjustingQuantity = false;
    },
    updatePcsFromQuantity(quantityValue) {
      if (!this.selectedProduct || !quantityValue) return;
      const pcsPerUnit = Number(
        this.selectedProduct.pcs != null
          ? this.selectedProduct.pcs
          : this.selectedProduct.items_per_pack != null
          ? this.selectedProduct.items_per_pack
          : 0
      );
      if (!pcsPerUnit) return;
      this.adjustingPcs = true;
      const pcs = Number(quantityValue) * pcsPerUnit;
      this.orderItem.pcs = Number.isFinite(pcs)
        ? Number(pcs.toFixed(2))
        : this.orderItem.pcs;
      this.adjustingPcs = false;
    },
  },
  mounted() {
    this.loadInitialData();
  },
};
</script>

<style scoped>
.load-order-builder {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.page-body {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.card-block {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.block-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.block-header h2 {
  margin: 0;
  font-size: 1.45rem;
  color: #0f172a;
}

.block-header p {
  margin: 0.2rem 0 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.block-actions {
  display: flex;
  gap: 0.75rem;
}

.block-subtitle {
  margin: -0.5rem 0 0;
  color: #6b7280;
  font-size: 0.95rem;
}

.product-selection {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.search-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input-group i.bi-search {
  position: absolute;
  left: 1rem;
  color: #6b7280;
  font-size: 1.1rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.85rem 1rem 0.85rem 3rem;
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 14px;
  font-size: 1rem;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.btn-clear-search {
  position: absolute;
  right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(148, 163, 184, 0.2);
  border-radius: 50%;
  color: #475569;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.btn-clear-search:hover {
  background: rgba(148, 163, 184, 0.3);
  color: #1e293b;
}

.btn-clear-search i {
  font-size: 0.85rem;
}

.search-results-info {
  margin: 0;
  font-size: 0.9rem;
  color: #6b7280;
  font-style: italic;
}

.product-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem 1rem;
  border: 1px dashed rgba(148, 163, 184, 0.4);
  border-radius: 18px;
  color: #6b7280;
  text-align: center;
}

.product-state i {
  font-size: 2rem;
  color: #2563eb;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
}

.product-card {
  background: #f8fafc;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 18px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;
}

.product-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.product-card:not(:disabled):hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.12);
  border-color: rgba(79, 70, 229, 0.6);
}

.product-image-wrapper {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #94a3b8;
  font-size: 2rem;
}

.product-card-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  text-align: center;
}

.product-card-body h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
  text-transform: uppercase;
}

.product-price {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2563eb;
}

.product-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1200;
}

.product-modal {
  width: min(520px, 100%);
  background: #ffffff;
  border-radius: 26px;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #0f172a;
}

.product-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.75rem 1.75rem 1.25rem;
}

.product-modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
}

.product-modal-header p {
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.btn.icon-only {
  padding: 0.5rem;
  border-radius: 12px;
}

.product-modal-body {
  padding: 0 1.75rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.product-modal-image {
  display: flex;
  justify-content: center;
}

.product-modal-image img {
  width: 140px;
  height: 140px;
  border-radius: 16px;
  object-fit: cover;
  box-shadow: 0 18px 35px rgba(15, 23, 42, 0.38);
}

.quantity-panels {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.quantity-display {
  background: #f8fafc;
  border-radius: 18px;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  border: 1px solid #e2e8f0;
}

.quantity-display span {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.quantity-display strong {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  font-family: "Roboto Mono", monospace;
}

.quantity-display.secondary {
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.3);
}

.quantity-keyboard {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.btn-keyboard {
  border: none;
  border-radius: 16px;
  padding: 0.85rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  background: linear-gradient(135deg, #4f46e5, #2563eb);
  box-shadow: 0 12px 22px rgba(79, 70, 229, 0.32);
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-keyboard:hover {
  transform: translateY(-2px);
  box-shadow: 0 16px 25px rgba(79, 70, 229, 0.36);
}

.btn-keyboard:active {
  transform: translateY(0);
}

.btn-keyboard.danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  box-shadow: 0 12px 22px rgba(239, 68, 68, 0.28);
}

.btn-keyboard.action {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 12px 22px rgba(16, 185, 129, 0.28);
}

.remark-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.remark-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
}

.remark-group textarea {
  border-radius: 14px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #0f172a;
  padding: 0.85rem 1rem;
  resize: vertical;
  min-height: 70px;
}

.remark-group textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.product-modal-footer {
  padding: 1.5rem 1.75rem 1.75rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.product-modal-footer .btn {
  min-width: 130px;
}

.form-group label {
  font-weight: 600;
  color: #475569;
}

.form-control {
  border-radius: 12px;
  border: 1px solid #d1d5db;
  padding: 0.7rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.items-cards-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.items-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  color: #6b7280;
  border: 1px dashed rgba(148, 163, 184, 0.4);
  border-radius: 18px;
}

.items-state i {
  font-size: 2rem;
  color: #2563eb;
}

.items-cards {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.item-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.item-image-wrapper {
  width: 60px;
  height: 60px;
  min-width: 60px;
  border-radius: 10px;
  overflow: hidden;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.item-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #cbd5e1;
  font-size: 1.5rem;
}

.item-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.item-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.item-main-info {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.item-main-info h4 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
}

.item-reference {
  font-size: 0.8rem;
  color: #64748b;
}

.item-details {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.item-detail {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.detail-label {
  font-size: 0.75rem;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.detail-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
}

.item-detail-total .detail-value {
  color: #0f172a;
  font-size: 1rem;
}

.total-value {
  color: #2563eb;
  font-weight: 700;
}

.btn-delete-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-delete-item:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.05);
}

.btn-delete-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-delete-item i {
  font-size: 1rem;
}

.items-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.08),
    rgba(79, 70, 229, 0.1)
  );
  border-radius: 12px;
  border: 1px solid rgba(37, 99, 235, 0.2);
}

.summary-total {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.summary-total span {
  font-size: 0.8rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.summary-total strong {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
}

.summary-count {
  font-size: 0.9rem;
  color: #475569;
  font-weight: 600;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 0.25rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.badge-light {
  background: rgba(37, 99, 235, 0.12);
  color: #1d4ed8;
}

.badge-dark {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.badge-primary {
  background: rgba(34, 197, 94, 0.16);
  color: #0f766e;
}

.order-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
}

.order-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.full {
  grid-column: 1 / -1;
}

.summary-card {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.1),
    rgba(79, 70, 229, 0.12)
  );
  color: #1e293b;
}

.summary-card span {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
}

.summary-card strong {
  font-size: 1.25rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-success {
  background: rgba(34, 197, 94, 0.16);
  color: #047857;
  border: none;
}

.btn-success:hover {
  background: rgba(34, 197, 94, 0.24);
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: none;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.error {
  color: #ef4444;
  font-size: 0.83rem;
}

.form-error-inline {
  grid-column: 1 / -1;
  margin: -0.25rem 0 0;
}

.has-error {
  border-color: #ef4444;
}

.hint {
  color: #6b7280;
  font-size: 0.85rem;
  text-align: center;
}

.text-danger {
  color: #ef4444;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

@media (max-width: 992px) {
  .load-order-builder {
    padding: 1.5rem 1.5rem 4rem;
  }
  .order-form {
    grid-template-columns: 1fr;
  }
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .btn {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .load-order-builder {
    padding: 1.25rem 1rem 4rem;
  }
  .card-block {
    padding: 1.5rem;
  }
  .item-details {
    gap: 0.75rem;
  }
  .item-image-wrapper {
    width: 50px;
    height: 50px;
    min-width: 50px;
  }
  .product-modal {
    border-radius: 20px 20px 0 0;
    max-height: 94vh;
  }
  .product-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }
  .product-modal-body {
    padding: 0 1.5rem 1.5rem;
  }
  .product-modal-footer {
    padding: 1.25rem 1.5rem 1.5rem;
  }
  .quantity-display strong {
    font-size: 1.6rem;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .card-block {
    padding: 1.25rem;
  }
  .btn {
    width: 100%;
  }
  .items-summary {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .item-details {
    gap: 0.5rem;
  }
  .item-image-wrapper {
    width: 45px;
    height: 45px;
    min-width: 45px;
  }
  .item-card {
    padding: 0.75rem;
    gap: 0.75rem;
  }
  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
  .quantity-display {
    padding: 1rem 1.1rem;
  }
  .btn-keyboard {
    padding: 0.75rem;
    font-size: 1.1rem;
  }
}
</style>
